<template>
  <div v-if="buttonLoading" class="text-center">
    <v-progress-circular indeterminate color="primary" />
  </div>
  <div v-else class="estate-content">
    <v-row class="back-arrow-container" no-gutters>
      <v-col cols="12">
        <router-link :to="{ name: 'GetFreeInsuranceQuote' }" class="back-arrow"
          ><v-icon large color="mid-grey" class="mt-1">{{
            icons.mdiArrowLeft
          }}</v-icon></router-link
        >
      </v-col>
    </v-row>

    <div class="estate-assist-title-section">
      <h1>
        {{ testator.firstNames }}, we’ve put together a personalised plan just
        for you.
      </h1>
      <h3>
        You now have exclusive access to Old Mutual's insurance products,
        thoughtfully designed to provide peace of mind in the event of your
        passing.
      </h3>
      <p>
        To explore your options, simply select "I'm interested" at the bottom of
        the page. If you’ve already made arrangements, you can proceed to
        download your will.
      </p>
    </div>
    <div
      class="estate-assist-container"
      :class="whitelabel.estateAssistEnabled ? 'estate-color' : 'cost-color'"
    >
      <div class="estate-assist-content">
        <div class="content-container">
          <div class="content-left">
            <Product
              :shadowed="false"
              :transparent="true"
              :slots="['header-with-image', 'description', 'button']"
            >
              <template v-slot:header-with-image>
                <ProductHeaderWithImage
                  :header="topAssistConfig.summaryProduct.header"
                ></ProductHeaderWithImage>
              </template>
              <template v-slot:description>
                <ProductDescription
                  :description="topAssistConfig.summaryProduct.descriptionText"
                  :large="true"
                  class="estate-assist-product-description"
                />
              </template>
              <template v-slot:button>
                <FunkButton
                  button-text="Learn More"
                  @buttonClicked="learnMoreClicked"
                  class="learn-more-button"
                ></FunkButton>
              </template>
            </Product>
            <RecommendedBenefitLevel
              v-if="whitelabel.estateAssistEnabled"
              @benefitToDisplay="benefitToDisplay"
              @benefitSelected="benefitSelected"
              :initialBenefitToDisplay="recommendedBenefit"
              :benefits="benefits"
              class="recommended-benefit-level-section"
            />
          </div>
          <div class="content-right">
            <div
              class="product-content-container"
              :class="{ 'expanded-one': index === benefit }"
              v-for="(product, index) in topAssistConfig.offeredProducts"
              :key="index"
            >
              <Product
                v-if="index === benefit"
                :shadowed="true"
                :slots="['title', 'description', 'button', 'package', 'info']"
              >
                <template v-slot:title>
                  <AssistProductBenefitTitle
                    class="title-padded-section"
                    :coloredText="product.header.coloredText"
                    :boldText="product.header.boldText"
                    :normalText="product.header.normalText"
                  />
                </template>
                <template v-slot:description>
                  <ProductDescription
                    :description="product.descriptionText"
                    :large="false"
                    class="estate-assist-product-description content-padded-section"
                  />
                </template>
                <template v-slot:button>
                  <div class="content-padded-section">
                    <FunkButton
                      button-text="Learn More"
                      @buttonClicked="learnMoreClicked('Cost')"
                      class="learn-more-button"
                    ></FunkButton>
                  </div>
                </template>
                <template v-slot:package>
                  <ProductPackage
                    :product="
                      whitelabel.estateAssistEnabled ? 'Estate' : 'Cost'
                    "
                    class="product-package-section"
                  >
                    <template v-slot:content>
                      <ProductPackageHorizontalContent
                        :product="
                          whitelabel.estateAssistEnabled ? 'Estate' : 'Cost'
                        "
                        :pkg="product.package"
                      />
                    </template>
                  </ProductPackage>
                </template>
                <template v-slot:info>
                  <div
                    style="
                      padding-left: 24px;
                      padding-top: 16px;
                      padding-bottom: 16px;
                    "
                  >
                    <ProductEstimateInfo
                      v-if="infoMessage(product.package)"
                      :message="infoMessage(product.package)"
                    />
                  </div>
                </template>
              </Product>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="benefits-heading-section" v-if="!testatorTooOld">
      <h1>
        We also offer our EstateAssist clients additional benefits that provide
        cover for education needs, cashflow and debt protection.
      </h1>
    </div>
    <div class="products-container" v-if="!testatorTooOld">
      <div
        class="product-container"
        v-for="(product, index) in topAssistConfig.bottomProducts"
        :key="index"
      >
        <Product :shadowed="true" :slots="['top-section', 'package']">
          <template v-slot:top-section>
            <div class="min-h-section">
              <ProductHeaderWithImage
                class="title-padded-section"
                :header="product.header"
              ></ProductHeaderWithImage>
              <ProductDescription
                :description="product.descriptionText"
                :large="false"
                class="product-description content-padded-section"
              />
              <div class="content-padded-section">
                <FunkButton
                  button-text="Learn More"
                  @buttonClicked="learnMoreClicked(product.product)"
                  class="learn-more-button"
                ></FunkButton>
              </div>
            </div>
          </template>
          <template v-slot:package>
            <div :ref="setProductRef" class="im-the-ref">
              <ProductPackage
                :product="product.header.coloredText"
                class="product-package-section"
              >
                <template v-slot:content>
                  <ProductPackageVerticalContent
                    :product="product.header.coloredText"
                    :packages="product.packages"
                    :testator="testator"
                    @cardExpanded="cardExpanded"
                    @updateInsurance="
                      params => $emit('updateInsurance', params)
                    "
                  />
                </template>
              </ProductPackage>
            </div>
          </template>
        </Product>
      </div>
    </div>
    <div class="products-container overflowed" v-if="!testatorTooOld">
      <div
        class="product-container"
        v-for="(product, index) in topAssistConfig.bottomSecondRowProducts"
        :key="index"
      >
        <Product
          v-if="product"
          :shadowed="true"
          :slots="['top-section', 'package']"
        >
          <template v-slot:top-section>
            <div class="min-h-section">
              <ProductHeaderWithImage
                class="title-padded-section"
                :header="product.header"
              ></ProductHeaderWithImage>
              <ProductDescription
                :description="product.descriptionText"
                :large="false"
                class="product-description content-padded-section"
              />
              <div class="content-padded-section">
                <FunkButton
                  button-text="Learn More"
                  @buttonClicked="learnMoreClicked(product.product)"
                  class="learn-more-button"
                ></FunkButton>
              </div>
            </div>
            <div>
              <div>{{ product.packages[0].capitalGainsTaxAmount }}</div>
              <div>{{ product.packages[0].combinedTaxAssistAmount }}</div>
              <div>{{ product.packages[0].taxAssistAmount }}</div>
            </div>
          </template>
          <template v-slot:package>
            <ProductPackage
              :product="product.header.coloredText"
              class="product-package-section"
            >
              <template v-slot:content>
                <ProductPackageVerticalContent
                  :product="product.header.coloredText"
                  :packages="product.packages"
                  :testator="testator"
                  @updateInsurance="params => $emit('updateInsurance', params)"
                />
              </template>
            </ProductPackage>
          </template>
        </Product>
        <ProductPlaceholder v-else></ProductPlaceholder>
      </div>
    </div>

    <div v-if="showEasterEgg">
      <div class="revealing-div">
        <pre>{{ JSON.stringify(startLeadBody, null, 2) }}</pre>
      </div>
    </div>

    <div class="button-container">
      <GradientButton
        class="interested-button"
        @gradientButtonClicked="interestedClicked"
        :title="`Yes I'm Interested`"
      />
      <GradientButton
        :title="`No thanks, I'll skip to my will`"
        @gradientButtonClicked="skipToWillClicked"
        :fill="false"
      />
      <GradientButton
        v-if="coverLimitModalOpen"
        :title="`Speak to an advisor`"
        @gradientButtonClicked="advisorClicked"
        :fill="false"
      />
    </div>
    <v-dialog
      class="request-a-call-modal"
      v-model="requestACallSuccessModal"
      max-width="440px"
      rounded="0"
    >
      <v-card rounded="0">
        <div class="request-a-call-modal-content">
          <div class="title">
            <div class="left">
              <h1>Thank you</h1>
            </div>
            <div class="right">
              <v-img
                @click="requestACallSuccessModal = false"
                aspect-ratio="1"
                :src="require('@/assets/shared/insurance/close_vector.svg')"
              />
            </div>
          </div>
          <div class="description">
            <p>An advisor will contact you.</p>
          </div>
          <div class="button-section">
            <GradientButton
              @gradientButtonClicked="downloadWillClicked"
              :title="`DOWNLOAD MY WILL`"
            />
          </div>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
  import OverviewProgressBars from '../overview/OverviewProgressBars.vue';
  import InsuranceCard from './InsuranceCard.vue';
  import Product from './Product.vue';
  import ProductPlaceholder from './ProductPlaceholder.vue';
  import RecommendedBenefitLevel from './RecommendedBenefitLevel.vue';
  import ProductHeaderWithImage from './ProductHeaderWithImage.vue';
  import ProductDescription from './ProductDescription.vue';
  import AssistProductBenefitTitle from './AssistProductBenefitTitle.vue';
  import ProductPackage from './ProductPackage.vue';
  import ProductPackageHorizontalContent from './ProductPackageHorizontalContent.vue';
  import ProductPackageVerticalContent from './ProductPackageVerticalContent.vue';
  import ProductEstimateInfo from './ProductEstimateInfo.vue';
  import { REQUEST_A_CALL_MUTATION } from '../../constants/graphql/testators';

  export default {
    name: 'InsuranceProducts',
    components: {
      OverviewProgressBars,
      InsuranceCard,
      RecommendedBenefitLevel,
      Product,
      ProductPlaceholder,
      ProductHeaderWithImage,
      ProductDescription,
      AssistProductBenefitTitle,
      ProductPackage,
      ProductPackageHorizontalContent,
      ProductPackageVerticalContent,
      ProductEstimateInfo,
    },
    props: {
      quotes: {
        required: true,
        type: Array,
      },
      peopleRelations: {
        type: Object,
        required: true,
      },
      testator: {
        type: Object,
        required: true,
      },
      buttonLoading: {
        default: false,
        type: Boolean,
      },
      startLeadBody: {
        required: false,
        type: Object,
      },
    },
    watch: {
      peopleRelations(newValue) {
        this.peopleRelationships = newValue;
      },
      testator(newValue) {
        this.coverLimitModalOpen = this.coverLimitReached(newValue);
      },
      quotes(newValue) {
        this.handleResize();
      },
    },
    created() {
      if (!this.quotes.length) {
        this.requestAQuote(false);
      }
      if (!this.whitelabel.estateAssistEnabled) {
        this.benefit = 0;
      }
      if (this.testator.age > 64) {
        this.testatorTooOld = true;
      }
    },
    data(props) {
      return {
        showEasterEgg: false,
        inputSequence: '',
        benefit: undefined,
        requestAQuoteLoading: true,
        testatorLoading: true,
        requestACallSuccessModal: false,
        coverLimitModalOpen: this.coverLimitReached(props.testator),
        peopleRelationships: props.peopleRelations,
        productRefs: [],
        windowWidth: window.innerWidth,
        testatorTooOld: false,
      };
    },
    mounted() {
      this.handleResize();
      window.addEventListener('resize', this.handleResize);
      window.addEventListener('keydown', this.handleKeyPress);
    },
    beforeDestroy() {
      window.removeEventListener('resize', this.handleResize);
      window.removeEventListener('keydown', this.handleKeyPress);
    },
    computed: {
      benefits() {
        return this.eacProducts.map(product => {
          return product.name;
        });
      },
      wrappedText() {
        return (
          this.$vuetify.breakpoint.width <=
          this.$vuetify.breakpoint.thresholds.xs
        );
      },
      bottomProducts() {
        if (this.testatorTooOld) {
          return [];
        }

        const taxAssistProduct = {
          header: {
            imageUrl: this.whitelabel.images.productsTaxAssist,
            coloredText: 'Tax',
            normalText: 'Assist',
            displayLarge: false,
          },
          descriptionText: `Upon your passing, your loved ones may be burdened with significant tax liabilities. TaxAssist ensures that funds are made available to assist in covering Estate Duty and Capital Gains taxes.`,
          packages: [
            {
              price: this.productTax.coverAmount,
              premium: this.productTax.monthlyPremium,
              capitalGainsTaxAmount: this.productTax.capitalGainsTaxAmount,
              combinedTaxAssistAmount: this.productTax.combinedTaxAssistAmount,
              taxAssistAmount: this.productTax.taxAssistAmount,
              code: 'TAC',
              titleLeft: 'Capital Gains Tax contribution',
              titleRight: 'Estate Duty',
              descriptionLeft: 'Amount paid on your death',
              descriptionRight: 'Amount paid on your death',
              leftPriceTitle: 'Payout',
              rightPriceTitle: 'Payout',
              extraBottomProduct: {
                title: 'Combined Premium',
                price: this.productTax.monthlyPremium,
                description: 'Amount you pay per month',
              },
            },
          ],
          product: 'Tax',
        };

        const educationAssistProduct = {
          header: {
            imageUrl: this.whitelabel.images.productsEducationAssist,
            coloredText: 'Edu',
            normalText: 'Assist',
            displayLarge: false,
          },
          descriptionText: `Give your children the future they deserve by protecting their education should you pass away. This cover will pay out an annual amount towards your children’s education expenses.`,
          packages: this.educationAssistChildren,
          product: 'Edu',
        };

        const debtAssistProduct = {
          header: {
            imageUrl: this.whitelabel.images.productsDebtAssist,
            coloredText: 'Debt',
            normalText: 'Assist',
            displayLarge: false,
          },
          descriptionText: `You may have outstanding loans on your house or car. This cover will make sure you have sufficient cash to pay off these loans should you pass away.`,
          packages: [
            {
              price: this.productDebt.coverAmount,
              premium: this.productDebt.monthlyPremium,
              code: 'DAC',
            },
          ],
          product: 'Debt',
        };

        const cashAssistProduct = {
          header: {
            imageUrl: this.whitelabel.images.productsCashAssist,
            coloredText: 'Cash',
            normalText: 'Assist',
            displayLarge: false,
          },
          descriptionText: `When you pass away, your family might need money immediately to cover day to day expenses e.g. groceries. This cover gives your loved ones instant access to money.`,
          packages: [
            {
              price: this.productCash.coverAmount,
              premium: this.productCash.monthlyPremium,
              code: 'CAC',
            },
          ],
          product: 'Cash',
        };

        if (
          this.productEducation?.childrenQuotes &&
          this.productEducation?.childrenQuotes !== '[]'
        ) {
          if (this.windowWidth > 959 && this.windowWidth <= 1263) {
            return [taxAssistProduct, educationAssistProduct];
          } else {
            return [
              taxAssistProduct,
              educationAssistProduct,
              debtAssistProduct,
            ];
          }
        } else {
          if (this.windowWidth > 959 && this.windowWidth <= 1263) {
            // return [taxAssistProduct, educationAssistProduct];
            return [taxAssistProduct, debtAssistProduct];
          } else {
            return [taxAssistProduct, debtAssistProduct, cashAssistProduct];
          }

          // return [taxAssistProduct, debtAssistProduct, cashAssistProduct];
        }
      },
      bottomSecondRowProducts() {
        const cashAssistProduct = {
          header: {
            imageUrl: this.whitelabel.images.productsCashAssist,
            coloredText: 'Cash',
            normalText: 'Assist',
            displayLarge: false,
          },
          descriptionText: `When you pass away, your family might need money immediately to cover day to day expenses e.g. groceries. This cover gives your loved ones instant access to money.`,
          packages: [
            {
              price: this.productCash.coverAmount,
              premium: this.productCash.monthlyPremium,
              code: 'CAC',
            },
          ],
          product: 'Cash',
        };

        const debtAssistProduct = {
          header: {
            imageUrl: this.whitelabel.images.productsDebtAssist,
            coloredText: 'Debt',
            normalText: 'Assist',
            displayLarge: false,
          },
          descriptionText: `You may have outstanding loans on your house or car. This cover will make sure you have sufficient cash to pay off these loans should you pass away.`,
          packages: [
            {
              price: this.productDebt.coverAmount,
              premium: this.productDebt.monthlyPremium,
              code: 'DAC',
            },
          ],
          product: 'Debt',
        };

        if (
          this.productEducation?.childrenQuotes &&
          this.productEducation?.childrenQuotes !== '[]'
        ) {
          if (this.windowWidth > 959 && this.windowWidth <= 1263) {
            return [debtAssistProduct, cashAssistProduct];
          } else {
            return [cashAssistProduct, null, null];
          }
        } else {
          if (this.windowWidth > 959 && this.windowWidth <= 1263) {
            return [cashAssistProduct, null];
          } else {
            return [null, null, null];
          }
        }
      },

      recommendedBenefit() {
        return 0;
      },
      eacProducts() {
        let filtered = this.quotes.filter(quote => {
          return quote.productCode === 'EAC';
        });
        if (filtered.length) {
          return filtered;
        } else {
          return [];
        }
      },
      productEstateStandard() {
        let filtered = this.quotes.filter(quote => {
          return quote.productCode === 'EAC';
        });
        if (filtered.length) {
          return filtered[0];
        } else {
          return {};
        }
      },
      productEstatePlus() {
        let filtered = this.quotes.filter(quote => {
          return quote.productCode === 'EAC';
        });
        if (filtered.length) {
          return filtered[1];
        } else {
          return {};
        }
      },
      productEstatePremium() {
        let filtered = this.quotes.filter(quote => {
          return quote.productCode === 'EAC';
        });
        if (filtered.length) {
          return filtered[2];
        } else {
          return {};
        }
      },
      productCash() {
        let filtered = this.quotes.filter(quote => {
          return quote.productCode === 'CAC';
        });
        if (filtered.length) {
          return filtered[0];
        } else {
          return {};
        }
      },
      productDebt() {
        let filtered = this.quotes.filter(quote => {
          return quote.productCode === 'DAC';
        });
        if (filtered.length) {
          return filtered[0];
        } else {
          return {};
        }
      },
      productTax() {
        let filtered = this.quotes.filter(quote => {
          return quote.productCode === 'TAC';
        });
        if (filtered.length) {
          return filtered[0];
        } else {
          return {};
        }
      },
      productCost() {
        let filtered = this.quotes.filter(quote => {
          return quote.productCode === 'COC';
        });
        if (filtered.length) {
          return filtered[0];
        } else {
          return {};
        }
      },
      productEducation() {
        let filtered = this.quotes.filter(quote => {
          return quote.productCode === 'EC_D';
        });
        if (filtered.length) {
          return filtered[0];
        } else {
          return {};
        }
      },
      educationAssistChildren() {
        let childrenArray = [];

        if (this.productEducation?.childrenQuotes !== undefined) {
          childrenArray = JSON.parse(this.productEducation?.childrenQuotes);
        }

        let children = [];
        if (this.productEducation?.childrenQuotes) {
          children = childrenArray.sort(
            (a, b) => b.cover_amount - a.cover_amount
          );
        }

        const objects = [];

        children.forEach(child => {
          const beChild = this.peopleRelationships.children.filter(
            c => c.id == child.child_id
          )[0];

          objects.push({
            name: beChild.displayName,
            price: child.cover_amount,
            premium: child.monthly_premium,
            code: 'EC_D',
            evaluation: beChild?.insuranceChildValidation?.evaluation,
            dateOfBirth: beChild?.dateOfBirth,
          });
        });

        return objects;
      },
      insurance() {
        return this.testator.insurance;
      },
      summaryProduct() {
        if (this.whitelabel.estateAssistEnabled) {
          return {
            header: {
              imageUrl: this.whitelabel.images.productsEstateAssist,
              coloredText: 'Estate',
              normalText: 'Assist',
              displayLarge: true,
            },
            descriptionText: `EstateAssist covers legal costs for estate administration, including executor fees, conveyancing, Master's fees, and trust setup costs (if applicable) when winding up your estate.`,
          };
        } else {
          return {
            header: {
              imageUrl: this.whitelabel.images.productsCostAssist,
              coloredText: 'Cost',
              normalText: 'Assist',
              displayLarge: true,
            },
            descriptionText: `There are costs associated with winding up your estate such as Executor's fees and administration costs. This cover will make sure cash is available to cover these expenses.`,
          };
        }
      },
      offeredProducts() {
        if (this.whitelabel.estateAssistEnabled) {
          if (!this.eacProducts) {
            return [];
          }

          return this.eacProducts.map(product => {
            return {
              header: {
                imageUrl: this.whitelabel.images.productsEstateAssist,
                coloredText: product.name,
                boldText: 'Estate',
                normalText: 'Assist',
                displayLarge: true,
              },
              descriptionText: `Provides cover of up to R  ${this.formatCurrency(
                product.coverLimit
              )} worth of estate administration services.`,
              package: {
                price: product.coverLimit,
                premium: product.monthlyPremium,
                code: 'EAC',
              },
            };
          });
        } else {
          return [
            {
              header: {
                imageUrl: this.productCost.imageUrl,
                coloredText: '',
                boldText: 'Cost',
                normalText: 'Assist',
                displayLarge: true,
              },
              descriptionText: this.wrappedText
                ? 'Cover ranges between R 20 000 and R 750 000.'
                : `Cover ranges between <span style="text-wrap: nowrap;">R 20 000</span> and <span style="text-wrap: nowrap;">R 750 000.</span>`,
              package: {
                price: this.productCost.coverAmount,
                premium: this.productCost.monthlyPremium,
                code: 'COC',
              },
            },
          ];
        }
      },
      topAssistConfig() {
        return {
          summaryProduct: this.summaryProduct,
          offeredProducts: this.offeredProducts,
          bottomProducts: this.bottomProducts,
          bottomSecondRowProducts: this.bottomSecondRowProducts,
        };
      },
    },
    methods: {
      handleKeyPress(event) {
        const targetSequence = 'revelio';

        // Add the pressed key to the input sequence
        this.inputSequence += event.key.toLowerCase();

        // Check if the input matches the target sequence
        if (this.inputSequence.endsWith(targetSequence)) {
          this.showEasterEgg = true;
        }

        // Limit the input sequence length to the target sequence length
        if (this.inputSequence.length > targetSequence.length) {
          this.inputSequence = this.inputSequence.slice(-targetSequence.length);
        }
      },
      formatCurrency(value) {
        return this.$options.filters.currency(value);
      },
      cardExpanded() {
        this.handleResize();
      },
      setProductRef(el) {
        if (el) {
          this.productRefs.push(el);
        }
      },
      handleResize() {
        if (window.matchMedia('(max-width: 959px)').matches) {
          this.clearHeights();
        } else {
          this.clearHeights();
          this.adjustHeights();
        }
        this.windowWidth = window.innerWidth;
      },
      adjustHeights() {
        this.$nextTick(() => {
          const maxHeight = Math.max(
            ...this.productRefs.map(ref => ref.offsetHeight)
          );

          this.productRefs.forEach(ref => {
            ref.style.height = `${maxHeight}px`;
          });
        });
      },
      clearHeights() {
        this.productRefs.forEach(ref => {
          ref.style.height = '';
        });
      },
      infoMessage(pkg) {
        if (pkg.evaluation === 'failed_age_validation') {
          return `${pkg.name} doesn't meet the age requirement.`;
        } else {
          if (
            pkg.code === 'COC' ||
            pkg.code === 'DAC' ||
            pkg.code === 'CAC' ||
            pkg.code === 'EAC'
          ) {
            if (pkg.price !== undefined && pkg.price !== 0) {
              return 'Estimated amount and not guaranteed';
            }
          }
        }
      },
      coverAmountForEstateProduct(product) {
        if (product === 'standard') {
          return 200000;
        } else if (product === 'plus') {
          return 300000;
        } else {
          return 550000;
        }
      },
      coverLimitReached(testator) {
        return testator.estateCostCalculation.coverLimitReached;
      },
      advisorClicked() {
        this.$apollo
          .mutate({
            mutation: REQUEST_A_CALL_MUTATION,
            variables: {
              testatorId: this.testatorId,
              forInsurance: true,
            },
          })
          .then(response => {
            response.data.requestACall
              ? this.showSuccessMessage()
              : this.displayError();
          })
          .catch(error => {
            this.displayError();
            throw error;
          });
      },
      downloadWillClicked() {
        this.skip();
      },
      showSuccessMessage() {
        this.requestACallSuccessModal = true;
      },
      displayError() {
        alert('failure');
      },
      skipToWillClicked() {
        this.skip();
      },
      skip() {
        const insuranceInputDetails = {
          testatorId: this.testator.id,
          skipInsurance: true,
        };
        const params = {
          insuranceInputDetails: insuranceInputDetails,
          redirectLocation: 'OverviewPage',
        };
        this.$emit('updateInsurance', params);
      },
      interestedClicked() {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'insurance-interested-buy-btn',
          omuserid: this.$store.getters?.userId,
        });
        this.$emit('startLead');
      },
      requestAQuote(skipLoading) {
        const insuranceInputDetails = {
          testatorId: this.testator.id,
          monthlyIncome: parseInt(this.insurance.monthlyIncome),
          smoker: this.insurance.smoker,
          educationLevel: this.insurance.educationLevel,
          chosenBenefitPackage: this.priceForSelectedBenefit(this.benefit),
        };
        const params = {
          insuranceInputDetails: insuranceInputDetails,
        };
        this.$emit('updateInsuranceAndRequestQuotes', params, skipLoading);
      },
      learnMoreClicked(product) {
        if (product === 'Edu') {
          this.$router.push({ name: 'LearnMore', hash: '#edu-assist' });
        } else if (product === 'Debt') {
          this.$router.push({ name: 'LearnMore', hash: '#debt-assist' });
        } else if (product === 'Cash') {
          this.$router.push({ name: 'LearnMore', hash: '#cash-assist' });
        } else if (product === 'Cost') {
          this.$router.push({ name: 'LearnMore', hash: '#cost-assist' });
        } else if (product === 'Tax') {
          this.$router.push({ name: 'LearnMore', hash: '#tax-assist' });
        } else {
          this.$router.push({ name: 'LearnMore' });
        }
      },
      benefitToDisplay(index) {
        this.benefit = index;
      },
      benefitSelected(index) {
        this.benefit = index;
        this.requestAQuote(true);
      },
      priceForSelectedBenefit(index) {
        if (index === 0) {
          return this.productEstateStandard.coverAmount;
        } else if (index === 1) {
          return this.productEstatePlus.coverAmount;
        } else if (index === 2) {
          return this.productEstatePremium.coverAmount;
        }
      },
    },
  };
</script>
<style>
  .v-dialog {
    border-radius: 0 !important;
  }
</style>
<style lang="scss" scoped>
  .request-a-call-modal-content {
    padding: 30px;

    .title {
      display: flex;
      justify-content: space-between;
      .left {
        h1 {
          font-size: 22px;
          font-weight: 600;
          line-height: 26.82px;
          color: #000000;
          font-family: 'Montserrat';
        }
      }
      .right {
        .v-image {
          cursor: pointer;
        }
      }
    }
    .description {
      margin-top: 16px;
      p {
        margin: 0;
        font-size: 16px;
        font-weight: 400;
        line-height: 22px;
        color: #282828;
      }
    }
    .button-section {
      margin-top: 32px;
    }
  }

  .estate-content {
    .revealing-div {
      background-color: lightgray;
      border-radius: 10px;
      padding: 20px;
      margin-top: 20px;
    }

    .back-arrow-container {
      margin-top: 14px;
    }

    .estate-assist-title-section {
      margin-top: 16px;
      padding-bottom: 62px;

      h1 {
        font-size: 38px;
        font-weight: 500;
        line-height: 46px;
        color: 000000;
        margin-bottom: 16px;
      }

      h3 {
        font-size: 22px;
        font-weight: 400;
        line-height: 26px;
        margin-bottom: 32px;
        color: #282828;
      }

      h2 {
        font-size: 28px;
        font-weight: 500;
        line-height: 34px;
        letter-spacing: -0.0025em;
        color: #282828;
        margin-bottom: 16px;
      }
      p {
        font-family: Montserrat;
        font-size: 16px;
        font-weight: 400;
        line-height: 22px;
        text-align: left;
        margin: 0;
        color: #282828;
      }
    }

    .benefits-heading-section {
      width: 100%;
      display: flex;
      justify-content: center;
      padding-top: 60px;
      padding-bottom: 40px;

      h1 {
        font-size: 20px;
        font-weight: 600;
        line-height: 30px;
        color: #282828;
      }
    }

    .title-padded-section {
      padding-left: 24px;
      padding-right: 24px;
      padding-top: 24px;
    }

    .content-padded-section {
      padding-left: 24px;
      padding-right: 24px;
    }

    .estate-assist-container {
      padding: 40px;
      width: 100%;

      .estate-assist-content {
        display: flex;
        flex-direction: column;
        width: 100%;
        height: 100%;
        box-shadow: 0px 24px 38px 0px #00000024;
        padding: 60px;

        .exception-banner-container {
          margin-bottom: 20px;
        }

        .content-container {
          display: flex;
          .content-left {
            flex: 1;
            max-width: 494px;

            .recommended-benefit-level-section {
              margin-top: 50px;
            }

            .estate-assist-product-description {
              margin-top: 5px;
            }
            .learn-more-button {
              margin-top: 24px;
            }
          }
          .content-right {
            flex: 1;
            display: flex;
            justify-content: flex-end;

            .product-content-container {
              max-width: 410px;
            }
          }
        }
      }

      .content-right {
        .estate-assist-product-description {
          margin-top: 14px;
        }
        .learn-more-button {
          margin-top: 20px;
        }

        .product-package-section {
          margin-top: 44px;
        }
      }
    }

    .products-container.overflowed {
      margin-top: 37px;
    }

    .products-container {
      width: 100%;
      display: flex;

      .product-container {
        flex: 1;
        margin-left: 16px;
        margin-right: 16px;

        .min-h-section {
          min-height: 330px;
        }
      }
      .product-container:first-of-type {
        margin-left: 0;
      }
      .product-container:last-of-type {
        margin-right: 0;
      }

      .product-description {
        margin-top: 16px;
      }
      .learn-more-button {
        margin-top: 20px;
        margin-bottom: 22px;
      }
      .product-package-section {
      }
    }

    .button-container {
      margin-top: 76px;
      display: flex;
      justify-content: center;
      gap: 22px;
    }
  }

  @media only screen and (min-width: 960px) and (max-width: 1263px) {
    .estate-content {
      .estate-assist-container {
        .estate-assist-content {
          .content-container {
            .content-left {
              max-width: 300px;

              .recommended-product-benefit-level-container {
                .recommended-product-benefit-levell-container {
                  display: none !important;
                  .recommended-product-benefit-container.benefit {
                    p {
                      font-size: 14px;
                      font-weight: 600;
                      line-height: 21px;
                    }
                  }
                }
              }
            }
            .content-right {
              .product-content-container {
                max-width: 300px;
              }
            }
          }
        }
      }
      .products-container {
        .product-container {
          .min-h-section {
            min-height: 402px;
          }
          .shadowed-product-container {
            .product-package-container {
              min-height: 209px;
            }
          }
        }
      }
    }
  }

  @media only screen and (min-width: 0px) and (max-width: 959px) {
    .estate-content {
      padding: 0px;

      .products-container.overflowed {
        margin-top: 0;
      }

      .back-arrow-container {
        margin-top: 18px;
      }

      .estate-assist-title-section {
        margin-top: 16px;
      }

      .estate-assist-container {
        padding: 12px;
        .estate-assist-content {
          padding: 0;

          .exception-banner-container {
            padding-top: 8px;
            padding-left: 8px;
            padding-right: 8px;
          }

          .content-container {
            flex-direction: column;
            padding: 24px;

            .content-left {
              max-width: none;
            }
            .content-right {
              max-width: none;
              .product-content-container {
                margin-top: 30px;
                max-width: none;
                width: 0;
              }
              .product-content-container.expanded-one {
                width: 100%;
              }
            }
          }
        }
      }

      .products-container {
        flex-direction: column;

        .product-container {
          padding-right: 12px;
          padding-left: 12px;
          margin-right: 0;
          margin-left: 0;
          margin-top: 20px;
        }
      }
    }
  }
</style>
<style lang="scss">
  @media only screen and (min-width: 0px) and (max-width: 959px) {
    .estate-content {
      .button-container {
        flex-direction: column;
        max-width: 240px;
      }
    }
    .product-package-container {
      section {
        width: 100%;

        .insurance-card-container {
          .insurance-card-container-section {
            div {
              width: 100%;
            }
          }
        }
      }
    }
  }
  @media only screen and (min-width: 0px) and (max-width: 1263px) {
    .estate-content {
      .estate-assist-container {
        .estate-assist-content {
          .content-container {
            .content-left {
              .recommended-product-benefit-level-container {
                .recommended-product-benefit-levell-container {
                  .recommended-product-benefit-container.benefit {
                    width: 98px;
                    height: 41px;
                    p {
                      font-size: 14px;
                      font-weight: 600;
                      line-height: 21px;
                    }
                  }
                  .recommended-product-benefit-container.benefit:first-of-type {
                    margin-right: 3px;
                  }
                  .recommended-product-benefit-container.benefit:last-of-type {
                    margin-left: 3px;
                  }
                }
              }
            }
          }
        }
      }
    }
  }
</style>
